import { useEffect, useState } from 'react';
import { PilotOnboardingService } from '@services/PilotOnboardingService';
import { getSafeTimezone, PilotCatalogTableRow } from './PilotOnboardingTableViewModel';

export interface UpdatePilotDetailResult {
    kind: 'success' | 'error';
    message: string;
}

// Rows use "N/D" as a display fallback — editing should start from an empty field instead.
export const displayValueToEditable = (value: string | undefined): string => {
    if (!value || value === 'N/D'.localized()) {
        return '';
    }

    return value;
};

export const isValidPilotCountryCode = (value: string): boolean => {
    return /^[A-Za-z]{2}$/.test(value.trim());
};

export const isValidPilotTimezone = (value: string): boolean => {
    return getSafeTimezone(value.trim()) !== undefined;
};

export const canSavePilotDetail = (
    name: string,
    countryCode: string,
    timezone: string,
    isDirty: boolean,
    isSaving: boolean,
): boolean => {
    return !isSaving
        && isDirty
        && Boolean(name.trim())
        && isValidPilotCountryCode(countryCode)
        && isValidPilotTimezone(timezone);
};

interface UsePilotOnboardingDetailModalViewModelProps {
    pilot: PilotCatalogTableRow | null;
    pilotOnboardingService: PilotOnboardingService;
}

export const usePilotOnboardingDetailModalViewModel = ({
    pilot,
    pilotOnboardingService,
}: UsePilotOnboardingDetailModalViewModelProps) => {
    const [name, setName] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [timezone, setTimezone] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const initialName = displayValueToEditable(pilot?.name);
    const initialCountryCode = displayValueToEditable(pilot?.countryCode);
    const initialTimezone = displayValueToEditable(pilot?.timezone);

    useEffect(() => {
        setName(displayValueToEditable(pilot?.name));
        setCountryCode(displayValueToEditable(pilot?.countryCode));
        setTimezone(displayValueToEditable(pilot?.timezone));
        setIsSaving(false);
    }, [pilot]);

    const isDirty = name.trim() !== initialName
        || countryCode.trim().toUpperCase() !== initialCountryCode.toUpperCase()
        || timezone.trim() !== initialTimezone;

    const canSave = canSavePilotDetail(name, countryCode, timezone, isDirty, isSaving);

    const save = async (): Promise<UpdatePilotDetailResult> => {
        if (!pilot?.organizationID) {
            return {
                kind: 'error',
                message: 'El piloto seleccionado no tiene un ID de organizacion valido.'.localized(),
            };
        }

        const trimmedName = name.trim();
        const normalizedCountryCode = countryCode.trim().toUpperCase();
        const trimmedTimezone = timezone.trim();

        setIsSaving(true);

        try {
            await pilotOnboardingService.updatePilot({
                organizationID: pilot.organizationID,
                name: trimmedName !== initialName ? trimmedName : undefined,
                countryCode: normalizedCountryCode !== initialCountryCode.toUpperCase()
                    ? normalizedCountryCode
                    : undefined,
                timezone: trimmedTimezone !== initialTimezone ? trimmedTimezone : undefined,
            });

            return {
                kind: 'success',
                message: 'Se actualizaron los datos del piloto.'.localized(),
            };
        } catch (error) {
            return {
                kind: 'error',
                message: error instanceof Error && error.message
                    ? error.message
                    : 'No se pudieron actualizar los datos del piloto. Intenta nuevamente.'.localized(),
            };
        } finally {
            setIsSaving(false);
        }
    };

    return {
        name,
        setName,
        countryCode,
        setCountryCode,
        timezone,
        setTimezone,
        isDirty,
        isSaving,
        canSave,
        save,
    };
};
